import React from 'react';
import type { ThemeFormValues } from '../ThemeEditorPanel';
import { SectionWrapper } from '../SectionWrapper';

const BUILTIN_DEFAULTS = {
  primaryColor: '#0d8080',
  secondaryColor: '#f59e0b',
  accentColor: '#f437a8',
  grayColor: '#e2e4e8',
  successColor: '#22c55e',
  warningColor: '#f59e0b',
  errorColor: '#ef4444',
  infoColor: '#3b82f6',
};

interface ColorPaletteSectionProps {
  form?: ThemeFormValues;
  onChange?: <K extends keyof ThemeFormValues>(key: K, value: ThemeFormValues[K]) => void;
  platformDefaults?: Partial<ThemeFormValues> | null;
}

export function ColorPaletteSection({ form, onChange, platformDefaults }: ColorPaletteSectionProps) {
  const editable = !!form && !!onChange;
  // Resolve defaults: platform overrides > built-in defaults
  const defaults = {
    primaryColor: platformDefaults?.primaryColor || BUILTIN_DEFAULTS.primaryColor,
    secondaryColor: platformDefaults?.secondaryColor || BUILTIN_DEFAULTS.secondaryColor,
    accentColor: platformDefaults?.accentColor || BUILTIN_DEFAULTS.accentColor,
    grayColor: platformDefaults?.grayColor || BUILTIN_DEFAULTS.grayColor,
    successColor: platformDefaults?.successColor || BUILTIN_DEFAULTS.successColor,
    warningColor: platformDefaults?.warningColor || BUILTIN_DEFAULTS.warningColor,
    errorColor: platformDefaults?.errorColor || BUILTIN_DEFAULTS.errorColor,
    infoColor: platformDefaults?.infoColor || BUILTIN_DEFAULTS.infoColor,
  };

  return (
    <SectionWrapper title="Color Palette">
        {/* Primary Colors */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Primary Colors (CTAs, Buttons, Links)</h3>
            {editable && (
              <InlineColorPicker
                value={form.primaryColor}
                onChange={(v) => onChange('primaryColor', v)}
                defaultColor={defaults.primaryColor}
              />
            )}
          </div>
          <PaletteGrid prefix="primary" />
        </div>

        {/* Secondary Colors */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Secondary Colors (Secondary Actions)</h3>
            {editable && (
              <InlineColorPicker
                value={form.secondaryColor}
                onChange={(v) => onChange('secondaryColor', v)}
                defaultColor={defaults.secondaryColor}
              />
            )}
          </div>
          <PaletteGrid prefix="secondary" />
        </div>

        {/* Accent Colors */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Accent Colors (Highlights, Special Elements)</h3>
            {editable && (
              <InlineColorPicker
                value={form.accentColor}
                onChange={(v) => onChange('accentColor', v)}
                defaultColor={defaults.accentColor}
              />
            )}
          </div>
          <PaletteGrid prefix="accent" />
        </div>

        {/* Gray Colors */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Gray Colors (Borders, Backgrounds)</h3>
            {editable && (
              <InlineColorPicker
                value={form.grayColor}
                onChange={(v) => onChange('grayColor', v)}
                defaultColor={defaults.grayColor}
              />
            )}
          </div>
          <PaletteGrid prefix="gray" />
        </div>

        {/* Status Colors */}
        {editable && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Status Colors</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatusColorEditor
                label="Success"
                value={form.successColor}
                onChange={(v) => onChange('successColor', v)}
                defaultColor={defaults.successColor}
                bgClass="bg-success-500"
              />
              <StatusColorEditor
                label="Warning"
                value={form.warningColor}
                onChange={(v) => onChange('warningColor', v)}
                defaultColor={defaults.warningColor}
                bgClass="bg-warning-500"
              />
              <StatusColorEditor
                label="Error"
                value={form.errorColor}
                onChange={(v) => onChange('errorColor', v)}
                defaultColor={defaults.errorColor}
                bgClass="bg-error-500"
              />
              <StatusColorEditor
                label="Info"
                value={form.infoColor}
                onChange={(v) => onChange('infoColor', v)}
                defaultColor={defaults.infoColor}
                bgClass="bg-info-500"
              />
            </div>
          </div>
        )}

        {/* Color Usage */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Color Usage Guidelines</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border bg-primary-50 border-primary-200">
              <h4 className="font-medium text-primary-900 mb-2">Primary Actions</h4>
              <p className="text-sm text-primary-700">Use primary colors for main CTAs and important actions.</p>
            </div>
            <div className="p-4 rounded-lg border bg-secondary-50 border-secondary-200">
              <h4 className="font-medium text-secondary-900 mb-2">Secondary Elements</h4>
              <p className="text-sm text-secondary-700">Use secondary colors for supportive elements and accents.</p>
            </div>
            <div className="p-4 rounded-lg border bg-accent-50 border-accent-200">
              <h4 className="font-medium text-accent-900 mb-2">Attention & Highlights</h4>
              <p className="text-sm text-accent-700">Use accent colors to draw attention to specific elements.</p>
            </div>
          </div>
        </div>
    </SectionWrapper>
  );
}

// ─── Palette Grid ──────────────────────────────────────────────────

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

function PaletteGrid({ prefix }: { prefix: string }) {
  return (
    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
      {SHADES.map(shade => (
        <div key={shade} className="text-center">
          <div
            className={`bg-${prefix}-${shade} ${shade <= 200 ? 'border' : ''} h-16 rounded-lg mb-1`}
          />
          <span className="text-xs">{shade}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Inline Color Picker ──────────────────────────────────────────

function InlineColorPicker({ value, onChange, defaultColor }: {
  value: string;
  onChange: (v: string) => void;
  defaultColor: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || defaultColor}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded border border-border cursor-pointer p-0.5"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || /^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
        }}
        placeholder={defaultColor}
        className="w-24 rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-mono"
      />
    </div>
  );
}

// ─── Status Color Editor ──────────────────────────────────────────

function StatusColorEditor({ label, value, onChange, defaultColor, bgClass }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  defaultColor: string;
  bgClass: string;
}) {
  return (
    <div className="p-3 rounded-lg border border-border bg-background">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-4 h-4 rounded-full ${bgClass}`} />
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || defaultColor}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-7 rounded border border-border cursor-pointer p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          placeholder={defaultColor}
          className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-mono"
        />
      </div>
    </div>
  );
}
